/**
 * @fileoverview Format Yardımcı Fonksiyonları
 *
 * Bu modül, fiyat, tarih ve karşılaştırma formatlaması için
 * yardımcı fonksiyonları içerir. Türkçe locale ve TRY para
 * birimi desteği sağlar.
 *
 * @module utils/format
 */

/**
 * HTML entity'lerini decode eder.
 *
 * API yanıtlarındaki encoded karakterleri (örn: &nbsp;, &amp;)
 * okunabilir metne çevirir.
 *
 * @param {string|null} text - Decode edilecek metin
 * @returns {string|null} Decode edilmiş metin
 *
 * @example
 * decodeHtmlEntities("Ev &amp; Bahçe")  // "Ev & Bahçe"
 * decodeHtmlEntities("100&nbsp;m²")      // "100 m²"
 */
export function decodeHtmlEntities(text) {
  if (!text || typeof text !== 'string') return text;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

/**
 * Harita marker'ları için kısa fiyat formatı oluşturur.
 *
 * Büyük sayıları okunabilir kısaltmalara çevirir:
 * - 1.000.000+ → "1M", "1.5M", "10M"
 * - 1.000-999.999 → "500K"
 *
 * @param {number|string} price - Formatlanacak fiyat
 * @returns {string|null} Kısaltılmış fiyat veya null
 *
 * @example
 * formatShortPrice(1500000)   // "1.5M"
 * formatShortPrice(500000)    // "500K"
 * formatShortPrice(15000000)  // "15M"
 */
export function formatShortPrice(price) {
  const value = typeof price === "number" ? price : Number(price);
  if (!Number.isFinite(value) || value <= 0) return null;

  if (value >= 1000000) {
    const m = value / 1000000;
    return m >= 10 ? `${Math.round(m)}M` : `${m.toFixed(1).replace('.0', '')}M`;
  }
  if (value >= 1000) {
    return `${Math.round(value / 1000)}K`;
  }
  return value.toString();
}

/**
 * Fiyatı Türkçe locale ile formatlar.
 *
 * Intl.NumberFormat kullanarak para birimi formatı uygular.
 * "TL" otomatik olarak "TRY"ye dönüştürülür.
 *
 * @param {number|string} price - Formatlanacak fiyat
 * @param {string} [currency="TRY"] - Para birimi kodu (TRY, USD, EUR)
 * @returns {string|null} Formatlanmış fiyat (örn: "₺5.000.000") veya null
 *
 * @example
 * formatPrice(5000000, "TRY")  // "₺5.000.000"
 * formatPrice(1500, "USD")     // "$1,500"
 * formatPrice(null)            // null
 */
export function formatPrice(price, currency) {
  const value = typeof price === "number" ? price : Number(price);
  if (!Number.isFinite(value)) return null;

  const normalizedCurrency =
    typeof currency === "string" && currency.toUpperCase() === "TL"
      ? "TRY"
      : currency;

  try {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: normalizedCurrency || "TRY",
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${value.toLocaleString("tr-TR")} ${currency || ""}`.trim();
  }
}

/**
 * ISO tarih string'ini Türkçe formatına çevirir.
 *
 * @param {string|null} iso - ISO 8601 formatında tarih
 * @returns {string|null} Türkçe formatlı tarih (örn: "15 Oca 2026") veya null
 *
 * @example
 * formatDate("2026-01-15T10:00:00Z")  // "15 Oca 2026"
 * formatDate(null)                      // null
 */
export function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("tr-TR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(d);
}

/**
 * İlan fiyatını bölge ortalamasıyla karşılaştırır.
 *
 * Fiyatın bölge ortalamasına göre durumunu açıklayan
 * Türkçe metin döndürür.
 *
 * @param {number|string} listingPrice - İlan fiyatı
 * @param {number|string} regionAveragePrice - Bölge ortalama fiyatı
 * @returns {string|null} Karşılaştırma metni veya null
 *
 * @example
 * formatRegionComparison(4000000, 5000000)  // "Bölge ortalamasının %20 altında"
 * formatRegionComparison(5500000, 5000000)  // "Bölge ortalamasının %10 üstünde"
 * formatRegionComparison(5100000, 5000000)  // "Bölge ortalamasına yakın" (<%5)
 */
export function formatRegionComparison(listingPrice, regionAveragePrice) {
  const price =
    typeof listingPrice === "number" ? listingPrice : Number(listingPrice);
  const avg =
    typeof regionAveragePrice === "number"
      ? regionAveragePrice
      : Number(regionAveragePrice);

  if (!Number.isFinite(price) || !Number.isFinite(avg) || avg <= 0) return null;

  const delta = (price - avg) / avg;
  const abs = Math.abs(delta);

  if (abs < 0.05) return "Bölge ortalamasına yakın";

  const pct = Math.round(abs * 100);
  return delta < 0
    ? `Bölge ortalamasının %${pct} altında`
    : `Bölge ortalamasının %${pct} üstünde`;
}

/**
 * Format a number with Turkish locale separators
 * @param {number|string|null|undefined} value - Value to format
 * @returns {string} Formatted number string (e.g., "1.234.567")
 */
export function formatNumber(value) {
  if (value == null) return "0";
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return "0";
  return num.toLocaleString("tr-TR");
}
